import React, { Fragment, useMemo, useRef, useState } from 'react'

import createLinkPlugin from '@draft-js-plugins/anchor'
import {
  BlockquoteButton,
  BoldButton,
  ItalicButton,
  OrderedListButton,
  UnderlineButton,
  UnorderedListButton,
} from '@draft-js-plugins/buttons'
import Editor from '@draft-js-plugins/editor'
import createMentionPlugin from '@draft-js-plugins/mention'
import '@draft-js-plugins/mention/lib/plugin.css'
import createToolbarPlugin, { Separator } from '@draft-js-plugins/static-toolbar'
import { Dialog, Transition } from '@headlessui/react'
import { ContentState, EditorState, convertFromHTML, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'

import { useStreamingNotes } from './useStreamingNotes'
import { customEntityTransform, suggestionsFilter } from './utils'
import { useGetStreamingNotesByApplicationQuery } from '@/services/streamingNote'
import { StreamingNote, UserData } from '@/types/unlock'

interface EditNoteModal {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  message: StreamingNote
  updateStreamingNote: (payload: any) => void
}

export function EditNoteModal({ isOpen, setIsOpen, message, updateStreamingNote }: EditNoteModal) {
  const editor = useRef<Editor>(null)
  const editorWrapperEl = useRef<HTMLDivElement>(null)
  const { isFetching, refetch } = useGetStreamingNotesByApplicationQuery({
    only_parent: true,
    application_id: message?.application,
  })
  const [open, setOpen] = useState(false)
  const { mentions } = useStreamingNotes()
  const [suggestions, setSuggestions] = useState(mentions)

  const [editorState, setEditorState] = useState(() => {
    const blocksFromHTML = convertFromHTML(message.text)
    const state = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    )
    return EditorState.createWithContent(state)
  })

  const { plugins, MentionSuggestions, Toolbar, linkPlugin } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({ entityMutability: 'IMMUTABLE' })
    const { MentionSuggestions } = mentionPlugin

    const toolbarPlugin = createToolbarPlugin()
    const { Toolbar } = toolbarPlugin

    const linkPlugin = createLinkPlugin({
      placeholder: 'Paste a link and press enter',
      linkTarget: '_blank',
    })

    const plugins = [mentionPlugin, toolbarPlugin, linkPlugin]
    return {
      plugins,
      MentionSuggestions,
      Toolbar,
      linkPlugin,
    }
  }, [])

  const onChange = (editorState: EditorState) => {
    setEditorState(editorState)
  }

  const onOpenChange = (open: boolean) => {
    setOpen(open)
  }

  const onSearchChange = ({ value }: Record<string, string>) => {
    setSuggestions(suggestionsFilter(value, mentions))
  }

  const handleSubmit = async () => {
    const rawContentState = convertToRaw(editorState.getCurrentContent())

    const hasMentions = Object.values(rawContentState.entityMap).some(
      (entity) => entity.type === 'mention'
    )

    const uniqueTaggedUsers = new Set<UserData>()

    if (hasMentions) {
      for (const entity of Object.values(rawContentState.entityMap)) {
        if (entity.type === 'mention') {
          uniqueTaggedUsers.add(entity.data.mention.id)
        }
      }
    }

    await updateStreamingNote({
      uuid: message.uuid,
      text: draftToHtml(rawContentState, null, null, customEntityTransform),
      tagged_users: Array.from(uniqueTaggedUsers),
    })

    refetch()

    setIsOpen(false)
  }

  return (
    <Transition
      appear
      show={isOpen}
      as={Fragment}
    >
      <Dialog
        as='div'
        className='relative z-20'
        onClose={() => setIsOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
                <div className='mt-2 mb-6 text-sm text-gray-400'>
                  <div className='border border-gray-200 rounded p-3 prose prose-sm min-w-full'>
                    <div ref={editorWrapperEl}>
                      <Toolbar>
                        {(externalProps) => (
                          <>
                            <BoldButton {...externalProps} />
                            <ItalicButton {...externalProps} />
                            <UnderlineButton {...externalProps} />
                            <linkPlugin.LinkButton {...externalProps} />
                            <Separator />
                            <UnorderedListButton {...externalProps} />
                            <OrderedListButton {...externalProps} />
                            <BlockquoteButton {...externalProps} />
                          </>
                        )}
                      </Toolbar>
                      <Editor
                        editorState={editorState}
                        onChange={onChange}
                        plugins={plugins}
                        ref={editor}
                        data-test='note-editor'
                        spellCheck
                      />
                      <MentionSuggestions
                        open={open}
                        suggestions={suggestions}
                        onOpenChange={onOpenChange}
                        onSearchChange={onSearchChange}
                      />
                    </div>
                  </div>
                </div>

                <div className='mt-4'>
                  <button
                    data-test='confirm-delete'
                    type='button'
                    className='inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2'
                    onClick={handleSubmit}
                    disabled={isFetching}
                  >
                    Confirm
                  </button>
                  <button
                    data-test='cancel-delete'
                    type='button'
                    className='inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ml-2'
                    onClick={() => setIsOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
